import React, { useContext, useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import { Check, ChevronRight } from "lucide-react";
import { BotTemplateData, LanguageNameSpaces } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import IconWithFallback from "Global/IconWithFallback";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";
import usePublicBotCard from "features/bots/PublicBots/usePublicBotCard.ts";
import PublicBotDrawer from "features/bots/PublicBots/PublicBotDrawer.tsx";
import NewLookAreaChart from "features/bots/NewLook/NewLookAreaChart";
import {
  canDrawSparkline,
  onCardKeyDown,
} from "features/bots/NewLook/newLookBotUtils";
import { newLookChip } from "features/bots/NewLook/newLookStyles";
import {
  getPublicBotChips,
  toPercentText,
} from "features/bots/PublicBots/publicBotDisplay";
import {
  BotName,
  BotSub,
  BotSubDot,
  BotSubPart,
  CopyBotButton,
} from "features/bots/PublicBots/newLookPublicBotStyles";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import { PUBLIC_BOT_CARD_ID, PUBLIC_BOT_TOTAL_PROFIT_ID } from "testIds.ts";

interface NewLookPublicBotRowProps {
  botData: BotTemplateData;
  id?: string;
  botRank?: number;
  /** The bot whose configuration the form uses now. */
  isApplied?: boolean;
  /** A hairline above the row (every row but the list's first). */
  divided?: boolean;
  /** Outside the bot form (the onboarding's phone list): the row opens the
   * bot like the card does, with a chevron instead of "Copy". */
  onBotClick?: () => void;
  prepareAccountOnClick?: boolean;
  /** A compact card for a sideways list: name over the result and trend. */
  tile?: boolean;
}

/**
 * New look: a public bot as a row in the bot form's "Public Bots" tab — the
 * list card's figures, compared at a glance: who it is, its accumulated
 * profit, its last 90 days, its total profit (the lead figure, the period of
 * the chart), and the copy action. In a narrow list (the wide screens' rail)
 * the figures and the chart take a line of their own under the name. The
 * row opens the bot's drawer.
 */
const NewLookPublicBotRow = ({
  botData,
  id,
  botRank,
  isApplied,
  divided,
  onBotClick,
  prepareAccountOnClick,
  tile,
}: NewLookPublicBotRowProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const tokens = getNewLookTokens(useTheme());
  const { isDrawer } = useContext(DrawerContext) || {};
  const {
    formattedChartData,
    onBotCardClick,
    onCopyClick,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    publicBotDrawerOpen,
    setPublicBotDrawerOpen,
    showNoExchangeModal,
  } = usePublicBotCard({
    botData,
    botRank,
    onBotClick,
    prepareAccountOnClick,
    isBotForm: !onBotClick,
  });

  const symbol = botData.symbols?.[0];
  const sub = [
    symbol,
    ...getPublicBotChips(botData),
    `${botData.days_running} ${t("public_bots.days")}`,
  ].filter(Boolean);
  const sparkline = useMemo(
    () => formattedChartData.map((point) => point.y),
    [formattedChartData]
  );

  return (
    <>
      <Row
        id={id}
        tabIndex={0}
        onClick={onBotCardClick}
        onKeyDown={onCardKeyDown(onBotCardClick)}
        $applied={isApplied}
        $divided={divided}
        $gutter={tile ? 14 : isDrawer ? 16 : 20}
        data-tile={tile || undefined}
        data-testid={PUBLIC_BOT_CARD_ID}
      >
        <Identity>
          <IconWithFallback
            iconType={ICON_TYPES.COIN}
            coinOrExchangeName={symbol}
            iconStyle={ICON_STYLE}
          />
          <Names>
            <RowName title={botData.name}>{botData.name}</RowName>
            <RowSub>
              {sub.map((part, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <BotSubDot />}
                  <BotSubPart title={part}>{part}</BotSubPart>
                </React.Fragment>
              ))}
            </RowSub>
          </Names>
        </Identity>
        <Spark aria-hidden={true}>
          {canDrawSparkline(sparkline) && (
            <NewLookAreaChart values={sparkline} height={tile ? 28 : 36} />
          )}
        </Spark>
        <Recent>
          <RecentFigure
            style={{
              color: directionColor(tokens, botData.last_90_days_change),
            }}
          >
            {toPercentText(botData.last_90_days_change)}
          </RecentFigure>
          <Meta>{t("public_bots.last_90_days")}</Meta>
        </Recent>
        <Result data-testid={PUBLIC_BOT_TOTAL_PROFIT_ID}>
          <Figure style={{ color: directionColor(tokens, botData.change) }}>
            {toPercentText(botData.change)}
          </Figure>
          <Meta>{t("public_bots.total_profit")}</Meta>
        </Result>
        <Action $always={!!onBotClick}>
          {onBotClick ? (
            <ChevronRight
              size={16}
              strokeWidth={1.75}
              color={tokens.muted}
              aria-hidden
            />
          ) : isApplied ? (
            <Applied>
              <Check size={12} strokeWidth={2} />
              {t("public_bots.copied")}
            </Applied>
          ) : (
            <CopyBotButton type="button" onClick={onCopyClick}>
              {t("public_bots.copy_bot")}
            </CopyBotButton>
          )}
        </Action>
      </Row>
      {showNoExchangeModal && (
        <NoExchangeConnectedModal
          placement="public_bot"
          onClose={() => setNoExchangeModalVisible(false)}
          action={t("public_bots.copy_a_bot")}
          open={noExchangeModalVisible}
        />
      )}
      {publicBotDrawerOpen && (
        <PublicBotDrawer
          open={publicBotDrawerOpen}
          closeDrawer={() => setPublicBotDrawerOpen(false)}
          botData={botData}
          onCopy={isApplied ? undefined : onCopyClick}
        />
      )}
    </>
  );
};

export default React.memo(NewLookPublicBotRow);

const ICON_STYLE = { width: 32, height: 32, marginRight: 0, flex: "none" };
/** The bot form's public bots list (FormPublicBots) names this container. */
const NARROW_LIST = "@container public-bots-list (max-width: 480px)";

const Row = styled.div<{
  $applied?: boolean;
  $divided?: boolean;
  $gutter: number;
}>`
  container-type: inline-size;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 112px auto auto auto;
  align-items: center;
  column-gap: 16px;
  min-height: 64px;
  padding: 12px ${(p) => p.$gutter}px;
  ${NARROW_LIST} {
    grid-template-columns: auto auto minmax(0, 1fr) auto;
    grid-template-areas:
      "identity identity identity action"
      "result recent spark spark";
    row-gap: 14px;
    padding-top: 16px;
    padding-bottom: 16px;
  }
  outline: none;
  cursor: pointer;
  transition: background-color 120ms;
  ${(p) =>
    p.$divided &&
    css`
      box-shadow: inset 0 1px 0 ${getNewLookTokens(p.theme).hairline};
    `}
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
  }
  ${(p) =>
    p.$applied &&
    css`
      background: ${getNewLookTokens(p.theme).well};
      box-shadow: inset 2px 0 0 ${getNewLookTokens(p.theme).accent};
    `}
  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr) auto auto;
    column-gap: 12px;
  }
  /* Tile: who it is over its result and trend. */
  &[data-tile] {
    grid-template-columns: minmax(0, 1fr) 72px 16px;
    grid-template-areas: "identity identity action" "result spark spark";
    row-gap: 12px;
    column-gap: 12px;
    align-items: start;
  }
`;
/** Up to two lines, so a long name isn't cut on a narrow row. */
const RowName = styled(BotName)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  overflow-wrap: anywhere;
  [data-tile] & {
    -webkit-line-clamp: 1;
  }
`;
/** Wraps between whole parts instead of cutting them. */
const RowSub = styled(BotSub)`
  flex-wrap: wrap;
  row-gap: 0;
  white-space: normal;
  > span {
    overflow: visible;
    white-space: nowrap;
  }
  [data-tile] & {
    flex-wrap: nowrap;
    overflow: hidden;
    > * {
      flex-shrink: 0;
    }
  }
`;
// Phones: the row opens the bot's drawer, whose footer copies it (outside
// the form, its chevron stays).
const Action = styled.div<{ $always?: boolean }>`
  display: flex;
  @media (max-width: 560px) {
    display: ${(p) => (p.$always ? "flex" : "none")};
  }
  ${NARROW_LIST} {
    grid-area: action;
  }
  [data-tile] > && {
    grid-area: action;
    justify-self: end;
    align-self: center;
  }
`;
const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  ${NARROW_LIST} {
    grid-area: identity;
  }
  [data-tile] > & {
    grid-area: identity;
  }
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Spark = styled.div`
  min-width: 0;
  @media (max-width: 560px) {
    display: none;
  }
  ${NARROW_LIST} {
    grid-area: spark;
  }
  [data-tile] > && {
    grid-area: spark;
    display: block;
    align-self: end;
  }
`;
const Result = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
  ${NARROW_LIST} {
    grid-area: result;
    align-items: flex-start;
    text-align: left;
  }
  [data-tile] > & {
    grid-area: result;
    align-items: flex-start;
    text-align: left;
  }
`;
/** The bot's last 90 days, beside its total (not on phones or tiles). */
const Recent = styled(Result)`
  @media (max-width: 560px) {
    display: none;
  }
  ${NARROW_LIST} {
    grid-area: recent;
  }
  [data-tile] > && {
    display: none;
  }
`;
const Figure = styled.span`
  ${newLookType.figureLarge};
  white-space: nowrap;
  [data-tile] & {
    ${newLookType.figure};
  }
`;
const RecentFigure = styled.span`
  ${newLookType.figure};
  white-space: nowrap;
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const Applied = styled.span`
  ${newLookChip};
  gap: 4px;
  height: 30px;
  padding: 0 10px;
`;
